/**
 * Impostazioni, sezione «Come lavora Otto»: quello che Otto ha imparato
 * dell'host.
 */

import { useEffect, useState } from "react";
import { Brain, X } from "lucide-react";
import { api, type PreferenzaView } from "../../lib/api.ts";
import { messaggioDi } from "../../lib/errori.ts";
import { Card } from "../../ui/primitives.tsx";

/**
 * Quello che Otto ha imparato dell'host, con il tasto per togliercelo.
 *
 * Qui dentro finiscono frasi che l'host non ha scritto ma che Otto ha dedotto
 * da quello che ha scritto. Una memoria che si può solo aggiungere, e solo a
 * voce, è una memoria che nessuno corregge: si accumula, finisce nel prompt di
 * ogni conversazione e peggiora le risposte senza che si capisca perché.
 *
 * Ognuna mostra la frase da cui è nata. È il pezzo che rende l'elenco
 * verificabile invece che misterioso: se una preferenza è sbagliata, si vede
 * subito se è sbagliata la deduzione o se l'host aveva detto davvero così.
 */
export function Memoria() {
  const [preferenze, setPreferenze] = useState<PreferenzaView[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  const carica = () =>
    api
      .preferenze()
      .then((r) => setPreferenze(r.preferenze))
      .catch((e: unknown) => setErrore(messaggioDi(e)));

  useEffect(() => {
    void carica();
    // Una volta sola all'apertura: non è un dato che cambia mentre guardi.
  }, []);

  const dimentica = async (p: PreferenzaView) => {
    // Ottimistico: togliere una riga è reversibile ridicendola a Otto, e
    // aspettare il giro di rete per un'operazione così piccola si sente.
    setPreferenze((prima) => (prima ?? []).filter((x) => x.id !== p.id));
    try {
      await api.dimenticaPreferenza(p.id);
    } catch (e: unknown) {
      setErrore(messaggioDi(e));
      void carica();
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        <Brain size={15} className="text-ink-faint" />
        <p className="text-[15px] font-bold text-ink">Cosa ho imparato di te</p>
      </div>
      <p className="mt-0.5 text-[13px] leading-relaxed text-ink-soft">
        Le cose che mi hai detto e che valgono anche domani, così non me le devi
        ripetere. Le uso in ogni conversazione. Se una non ti torna, toglila.
      </p>

      {errore ? (
        <p className="mt-3 text-[13px] text-danger">{errore}</p>
      ) : preferenze == null ? (
        <p className="mt-3 text-[13px] text-ink-faint">Guardo…</p>
      ) : preferenze.length === 0 ? (
        <p className="mt-3 text-[13px] text-ink-faint">
          Ancora niente. Dimmi in chat come preferisci che lavori — «rispondi
          più corto», «agli ospiti dammi del tu» — e me lo segno.
        </p>
      ) : (
        <ul className="mt-3 flex flex-col divide-y divide-line">
          {preferenze.map((p) => (
            <li key={p.id} className="flex items-start gap-3 py-2.5 first:pt-0">
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] leading-snug text-ink">
                  {p.testo}
                </span>
                {p.origine ? (
                  <span className="mt-0.5 block text-[12px] leading-snug text-ink-faint">
                    da: «{p.origine}»
                  </span>
                ) : null}
              </span>
              <button
                type="button"
                aria-label={`Dimentica: ${p.testo}`}
                onClick={() => void dimentica(p)}
                className="grid size-7 shrink-0 place-items-center rounded-field text-ink-faint transition-colors duration-quick hover:bg-surface-muted hover:text-danger"
              >
                <X size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
